import { Action } from 'redux';
import {
	SceneHelperData,
	SceneItemCoverConnection,
	SceneItemData,
	PopOverSceneItemData,
	SofaConfiguration,
	RangeSettings,
} from 'sofa-reborn-react';
import { ModelData, ProductData, SceneObject } from '@ikea-aoa/component-view-3d';

// State
export interface SceneState {
	pointerOverDropArea: boolean;
	sceneItems: SceneItemData[];
	resetCameraRotation: boolean;
	dragSceneItemId?: string | null;
	overSceneItemId?: string | null;
	measurements?: string;
	animateCameraRotation?: number | null;
	selectedSceneItem?: PopOverSceneItemData | null;
	selectedSceneArticle?: string | null;
	removeSceneItem?: SceneItemData | null;
	removeAllSceneItems?: boolean;
	rotateSceneItem?: SceneItemData | null;
	moveBackrest?: SceneItemData | null;
	moveBackrestAsArmrest?: SceneItemData | null;
	backrestAsArmrest?: SceneItemData | null;
	disableMoveBackrestAsArmrest?: boolean;
	disableMoveBackrest?: boolean;
	specificSelectedItems?: Map<string, SceneObject>;
	setSceneItemCover?: SceneItemCoverConnection | null;
	colorForAll?: string | null;
	environmentalLighting?: string;
	lightIntensity?: number;
	backrestsMissingCushion?: boolean;
	addCushions?: boolean;
	sceneHelper?: SceneHelperData;
	keepSceneHeight?: boolean;
	showRotationIcon: boolean;
	noRotationIcon: boolean;
	keepModule: boolean;
}

// Actions
export const SET_SCENE_DATA = 'SET_SCENE_DATA';
export const SELECT_SCENE_ITEM = 'SELECT_SCENE_ITEM';
export const SELECT_SCENE_ARTICLE = 'SELECT_SCENE_ARTICLE';
export const REMOVE_SCENE_ITEM = 'REMOVE_SCENE_ITEM';
export const REMOVE_SCENE_ITEM_OUTSIDE_SCENE = 'REMOVE_SCENE_ITEM_OUTSIDE_SCENE';
export const REMOVE_ALL_SCENE_ITEMS = 'REMOVE_ALL_SCENE_ITEMS';
export const ROTATE_SCENE_ITEM = 'ROTATE_SCENE_ITEM';
export const MOVE_BACKREST = 'MOVE_BACKREST';
export const DISABLE_MOVE_BACKREST = 'DISABLE_MOVE_BACKREST';
export const MOVE_BACKREST_AS_ARMREST = 'MOVE_BACKREST_AS_ARMREST';
export const DISABLE_MOVE_BACKREST_AS_ARMREST = 'DISABLE_MOVE_BACKREST_AS_ARMREST';
export const BACKREST_AS_ARMREST = 'BACKREST_AS_ARMREST';
export const SET_SCENE_ITEM_COVER = 'SET_SCENE_ITEM_COVER';
export const SET_COLOR_FOR_ALL = 'SET_COLOR_FOR_ALL';
export const DRAG_SCENE_ITEM_ID = 'DRAG_SCENE_ITEM_ID';
export const OVER_SCENE_ITEM_ID = 'OVER_SCENE_ITEM_ID';
export const ADD_CUSHIONS = 'ADD_CUSHIONS';
export const SET_ENVIRONMENTAL_LIGHTING = 'SET_ENVIRONMENTAL_LIGHTING';
export const SET_LIGHT_INTENSITY = 'SET_LIGHT_INTENSITY';
export const SET_MEASUREMENTS = 'SET_MEASUREMENTS';
export const ANIMATE_CAMERA_ROTATION = 'ANIMATE_CAMERA_ROTATION';
export const POINTER_OVER_DROP_AREA = 'POINTER_OVER_DROP_AREA';
export const SET_HELPER_POSITION = 'SET_HELPER_POSITION';
export const RESET_CAMERA_ROTATION = 'RESET_CAMERA_ROTATION';
export const SET_KEEP_SCENE_HEIGHT = 'SET_KEEP_SCENE_HEIGHT';
export const SHOW_ROTATION_ICON = 'SHOW_ROTATION_ICON';
export const SET_NO_ROTATION_ICON = 'SET_NO_ROTATION_ICON';
export const SET_KEEP_MODULE = 'SET_KEEP_MODULE';
export const SET_ROTATE_CAMERA = 'SET_ROTATE_CAMERA';
export const SET_SPECIFIC_SELECTED_ITEMS = 'SET_SPECIFIC_SELECTED_ITEMS';

interface SetSceneData extends Action {
	type: typeof SET_SCENE_DATA;
	sceneItems: SceneItemData[];
	sofaConfiguration: SofaConfiguration;
	articlesPlaceable: { [key: string]: boolean };
	backrestsMissingCushion: boolean;
	rangeSettings: RangeSettings;
	models: ModelData;
	products: ProductData;
	isOverDropArea: boolean;
}

interface SelectSceneItem extends Action {
	type: typeof SELECT_SCENE_ITEM;
	item?: PopOverSceneItemData | null;
}

interface SelectSceneArticle extends Action {
	type: typeof SELECT_SCENE_ARTICLE;
	value: string | null;
}

interface RemoveSceneItem extends Action {
	type: typeof REMOVE_SCENE_ITEM;
	item?: SceneItemData;
}

interface RemoveSceneItemOutsideScene extends Action {
	type: typeof REMOVE_SCENE_ITEM_OUTSIDE_SCENE;
	sceneObject?: SceneObject;
}

interface RemoveAllSceneItems extends Action {
	type: typeof REMOVE_ALL_SCENE_ITEMS;
	value: boolean;
}

interface RotateSceneItem extends Action {
	type: typeof ROTATE_SCENE_ITEM;
	item?: SceneItemData | null;
}

interface MoveBackrest extends Action {
	type: typeof MOVE_BACKREST;
	item?: SceneItemData | null;
}

interface DisableMoveBackrest extends Action {
	type: typeof DISABLE_MOVE_BACKREST;
	value?: boolean;
}

interface MoveBackrestAsArmrest extends Action {
	type: typeof MOVE_BACKREST_AS_ARMREST;
	item?: SceneItemData | null;
}

interface DisableMoveBackrestAsArmrest extends Action {
	type: typeof DISABLE_MOVE_BACKREST_AS_ARMREST;
	value?: boolean;
}

interface BackrestAsArmrest extends Action {
	type: typeof BACKREST_AS_ARMREST;
	item?: SceneItemData | null;
}

interface SetSceneItemCover extends Action {
	type: typeof SET_SCENE_ITEM_COVER;
	sceneItemCoverConnection: SceneItemCoverConnection | null;
}

interface SetColorForAll extends Action {
	type: typeof SET_COLOR_FOR_ALL;
	value: string | null;
}

interface DragSceneItemId extends Action {
	type: typeof DRAG_SCENE_ITEM_ID;
	value: string | null;
}

interface OverSceneItemId extends Action {
	type: typeof OVER_SCENE_ITEM_ID;
	value: string | null;
}

interface AddCushions extends Action {
	type: typeof ADD_CUSHIONS;
	value: boolean;
}

interface SetEnvironmentalLighting extends Action {
	type: typeof SET_ENVIRONMENTAL_LIGHTING;
	value: string;
}

interface SetLightIntensity extends Action {
	type: typeof SET_LIGHT_INTENSITY;
	value: number;
}

interface AnimateCameraRotation extends Action {
	type: typeof ANIMATE_CAMERA_ROTATION;
	value: number | null;
}

interface Measurements extends Action {
	type: typeof SET_MEASUREMENTS;
	value: string;
}

interface PointerOverDropArea extends Action {
	type: typeof POINTER_OVER_DROP_AREA;
	value: boolean;
}

interface SetSceneHelper extends Action {
	type: typeof SET_HELPER_POSITION;
	value: SceneHelperData;
}

interface ResetCameraRotation extends Action {
	type: typeof RESET_CAMERA_ROTATION;
	value: boolean;
}

interface SetKeepSceneHeight extends Action {
	type: typeof SET_KEEP_SCENE_HEIGHT;
	value: boolean;
}

interface ShowRotationIcon extends Action {
	type: typeof SHOW_ROTATION_ICON;
	value: boolean;
}

interface SetNoRotationIcon extends Action {
	type: typeof SET_NO_ROTATION_ICON;
	value: boolean;
}

interface SetKeepModule extends Action {
	type: typeof SET_KEEP_MODULE;
	value: boolean;
}

interface SetRotateCamera extends Action {
	type: typeof SET_ROTATE_CAMERA;
}

interface SetSpecificSelectedItems extends Action {
	type: typeof SET_SPECIFIC_SELECTED_ITEMS;
	items?: Map<string, SceneObject>;
}

export type SceneStateActions =
	| SetSceneData
	| SelectSceneItem
	| SelectSceneArticle
	| RemoveSceneItem
	| RemoveSceneItemOutsideScene
	| RemoveAllSceneItems
	| RotateSceneItem
	| MoveBackrest
	| DisableMoveBackrest
	| MoveBackrestAsArmrest
	| DisableMoveBackrestAsArmrest
	| BackrestAsArmrest
	| SetSceneItemCover
	| SetColorForAll
	| DragSceneItemId
	| OverSceneItemId
	| AddCushions
	| SetEnvironmentalLighting
	| SetLightIntensity
	| AnimateCameraRotation
	| Measurements
	| PointerOverDropArea
	| SetSceneHelper
	| ResetCameraRotation
	| SetKeepSceneHeight
	| ShowRotationIcon
	| SetNoRotationIcon
	| SetKeepModule
	| SetRotateCamera
	| SetSpecificSelectedItems;
